import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, RouterStateSnapshot } from '@angular/router';
import { NavController } from '@ionic/angular/standalone';
import { firstValueFrom } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { OrdersService } from '../commerce/orders.service';
import { CharityService } from '../charity/charity.service';
import { BusinessService } from '../business/business.service';
import { UserRolesService } from '../auth/user-roles.service';
import { AlertService } from '../navigation/alert.service';
import { Charity } from '../../models/Charity';
import { Business } from '../../models/Business';
import { Order } from '../../models/Order';

/**
 * Guard that loads order context and validates permissions before allowing access
 * to charity/business order detail pages. Checks if the user has permission for
 * the charity/business that owns the order, sets the appropriate context, and
 * blocks access with an alert if permission is denied.
 */
export const orderContextGuard: CanActivateFn = async (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
): Promise<boolean> => {
  const ordersService: OrdersService = inject(OrdersService);
  const charityService: CharityService = inject(CharityService);
  const businessService: BusinessService = inject(BusinessService);
  const userRolesService: UserRolesService = inject(UserRolesService);
  const alertService: AlertService = inject(AlertService);
  const navController: NavController = inject(NavController);

  // Extract order ID from route params
  const orderId: string | null = route.paramMap.get('id');

  if (!orderId) {
    console.error('[orderContextGuard] No order ID in route params');
    try {
      alertService.showAlertMessage('Error', 'Invalid order link.');
    } catch (err) {
      console.error('[orderContextGuard] Failed to show alert:', err);
    }
    navController.navigateRoot(['/main/home']);
    return false;
  }

  try {
    // Load the order
    const order: Order = await firstValueFrom(
      ordersService.getOrdersByOrderId(orderId)
    );

    // Validate order exists and has valid structure
    if (!order || !order.id) {
      console.warn('[orderContextGuard] Order not found or malformed:', orderId);
      try {
        alertService.showAlertMessage('Order Not Found', 'This order does not exist.');
      } catch (err) {
        console.error('[orderContextGuard] Failed to show alert:', err);
      }
      navController.navigateRoot(['/main/home']);
      return false;
    }

    // Determine context type based on route path
    const isCharityRoute: boolean = state.url.startsWith('/charity');
    const isBusinessRoute: boolean = state.url.startsWith('/business');

    // Wait for user roles to finish loading
    await firstValueFrom(
      userRolesService.rolesLoaded$.pipe(
        filter((loaded: boolean) => loaded),
        take(1)
      )
    );

    const businessId: number | undefined = order.businessId ?? order.product?.businessId;

    if (isBusinessRoute && businessId) {
      // Business order: validate permission and set business context
      const permittedBusinesses: Business[] = await firstValueFrom(
        userRolesService.permittedBusinesses$
      );

      const hasPermission: boolean = permittedBusinesses.some(
        (business: Business) => business.id === businessId
      );

      if (!hasPermission) {
        console.warn(
          '[orderContextGuard] User does not have permission for business:',
          businessId
        );
        try {
          alertService.showAlertMessage(
            'Access Denied',
            "You don't have permission to view this business's orders."
          );
        } catch (err) {
          console.error('[orderContextGuard] Failed to show alert:', err);
        }
        navController.navigateRoot(['/main/home']);
        return false;
      }

      // Set business context
      businessService.setCurrentBusiness(businessId.toString(), true);
    } else if (isCharityRoute && order.product?.charityId) {
      // Charity order: validate permission and set charity context
      const permittedCharities: Charity[] = await firstValueFrom(
        userRolesService.permittedCharities$
      );

      const hasPermission: boolean = permittedCharities.some(
        (charity: Charity) => Number(charity.id) === order.product.charityId
      );

      if (!hasPermission) {
        console.warn(
          '[orderContextGuard] User does not have permission for charity:',
          order.product.charityId
        );
        try {
          alertService.showAlertMessage(
            'Access Denied',
            "You don't have permission to view this charity's orders."
          );
        } catch (err) {
          console.error('[orderContextGuard] Failed to show alert:', err);
        }
        navController.navigateRoot(['/main/home']);
        return false;
      }

      // Set charity context
      charityService.setCurrentCharity(
        order.product.charityId.toString(),
        true
      );
    } else {
      // Edge case: route/order mismatch or missing data
      console.warn(
        '[orderContextGuard] Route/order context mismatch or missing data',
        {
          route: state.url,
          order,
          hasBusinessId: !!businessId,
          hasCharityId: !!order.product?.charityId,
        }
      );
      try {
        alertService.showAlertMessage('Error', 'Invalid order data.');
      } catch (err) {
        console.error('[orderContextGuard] Failed to show alert:', err);
      }
      navController.navigateRoot(['/main/home']);
      return false;
    }

    return true;
  } catch (error) {
    console.error('[orderContextGuard] Failed to load order context:', error);
    try {
      alertService.showAlertMessage(
        'Error Loading Order',
        'Unable to load order details. Please try again.'
      );
    } catch (err) {
      console.error('[orderContextGuard] Failed to show alert:', err);
    }

    // Always redirect to user portal on error
    navController.navigateRoot(['/main/home']);
    return false;
  }
};
